// packages/web/src/components/RecordForm.tsx — R2: the GENERIC record form.
// Given a record type KEY (it fetches that type's schema) plus an optional
// existing record (edit mode), it renders one input per field DRIVEN BY the
// field's declared type (text/number/currency/date/datetime/picklist/boolean/
// file) and one picker per relationship (search/select existing records of the
// target type, respecting cardinality one-vs-many). Submitting creates (POST
// /records) or edits (PATCH /records/:id) through the generic records engine,
// then reconciles relationship LINKS. Client validation is derived from the
// schema (required fields; picklist membership is inherent to the <select>).
// R3 wires this into every screen; R2 proves it on a handful of types.
import { useEffect, useMemo, useState } from 'preact/hooks';
import {
  ApiError,
  createRecord,
  updateRecord,
  linkRecord,
  unlinkRecord,
  uploadRecordBlob,
  listRecordsByType,
  getRecordTypeSchema,
  type RecordRow,
  type RecordTypeSchema,
  type SchemaField,
  type SchemaRelationship,
  type LinkRow,
} from '../lib/api';
import './RecordForm.css';

interface RecordFormProps {
  tenantId: string;
  typeKey: string;
  /** Edit mode: the record being edited + its existing links (for relationship
   * prefill). Omit for create mode. */
  existing?: { record: RecordRow; links: LinkRow[] };
  /** Optional pre-fetched schema (tests pass a mock; live callers omit it and
   * the form fetches). */
  schema?: RecordTypeSchema;
  /** CREATE mode only: seed initial field values (edit mode seeds from the
   * existing record instead). */
  prefillFields?: Record<string, unknown>;
  /** CREATE mode only: seed relationship selections, keyed by relationship key
   * — how a sub-record created from its parent detail pre-points at that
   * parent (edit mode seeds from the existing record's links instead). */
  prefillRels?: Record<string, string[]>;
  onSaved: (record: RecordRow) => void;
  onCancel?: () => void;
}

/** A relationship's currently-selected target ids, keyed by relationship key. */
type RelState = Record<string, string[]>;

function initialFieldValue(field: SchemaField, existing: Record<string, unknown> | undefined): unknown {
  if (existing && field.name in existing) return existing[field.name];
  if (field.default !== undefined) return field.default;
  return field.type === 'boolean' ? false : '';
}

/** Links whose relationship is declared on THIS record (src = this record) —
 * the ones a relationship picker owns. Keyed by relationship (link_type). */
function seedRelationships(recordId: string | undefined, links: LinkRow[]): RelState {
  const out: RelState = {};
  if (!recordId) return out;
  for (const l of links) {
    if (l.src_id !== recordId) continue;
    (out[l.link_type] ??= []).push(l.dst_id);
  }
  return out;
}

export function RecordForm(props: RecordFormProps) {
  const { tenantId, typeKey, existing, onSaved, onCancel } = props;
  const [schema, setSchema] = useState<RecordTypeSchema | null>(props.schema ?? null);
  const [schemaError, setSchemaError] = useState<string | null>(null);
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [rels, setRels] = useState<RelState>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [missing, setMissing] = useState<Set<string>>(new Set());

  // Fetch schema if not provided.
  useEffect(() => {
    if (props.schema) {
      setSchema(props.schema);
      return;
    }
    let alive = true;
    getRecordTypeSchema(tenantId, typeKey)
      .then((s) => alive && setSchema(s))
      .catch((e) => alive && setSchemaError(e instanceof ApiError ? e.message : 'Could not load the form.'));
    return () => {
      alive = false;
    };
  }, [tenantId, typeKey, props.schema]);

  // Seed field values + relationship selections once the schema is known. Edit
  // mode seeds from the existing record + its links; create mode seeds from the
  // optional prefill (else field defaults / empty).
  useEffect(() => {
    if (!schema) return;
    const seedSource = existing?.record.fields ?? props.prefillFields;
    const seeded: Record<string, unknown> = {};
    for (const f of schema.fields) seeded[f.name] = initialFieldValue(f, seedSource);
    setValues(seeded);
    setRels(
      existing ? seedRelationships(existing.record.id, existing.links) : props.prefillRels ?? {}
    );
  }, [schema, existing]);

  function setField(name: string, value: unknown) {
    setValues((v) => ({ ...v, [name]: value }));
  }

  if (schemaError) return <div class="rf rf--error" role="alert">{schemaError}</div>;
  if (!schema) return <div class="rf rf--loading">Loading…</div>;

  const isEdit = Boolean(existing);

  function validate(): string[] {
    const miss: string[] = [];
    for (const f of schema!.fields) {
      if (!f.required) continue;
      if (f.type === 'boolean') continue; // a required bool is satisfied by either value
      const v = values[f.name];
      const empty = v === undefined || v === null || v === '';
      const badNumber = (f.type === 'number' || f.type === 'currency') && !empty && Number.isNaN(Number(v));
      if (empty || badNumber) miss.push(f.name);
    }
    return miss;
  }

  /** Coerces a field's string form value to the JSON the engine expects. */
  function coerce(field: SchemaField, raw: unknown): unknown {
    switch (field.type) {
      case 'boolean':
        return Boolean(raw);
      case 'number':
      case 'currency':
        if (raw === '' || raw === null || raw === undefined) return undefined;
        return Number(raw);
      default:
        return raw === '' ? undefined : raw;
    }
  }

  function fieldsPayload(): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    for (const f of schema!.fields) {
      const c = coerce(f, values[f.name]);
      if (c !== undefined) out[f.name] = c;
    }
    return out;
  }

  /** Reconcile relationship links against the record: link newly-selected
   * targets, unlink removed ones (lookup only — master_detail unlinks are
   * refused by the engine and left in place). */
  async function reconcileLinks(recordId: string) {
    const existingLinks = existing?.links ?? [];
    for (const rel of schema!.relationships) {
      const wanted = rels[rel.key] ?? [];
      const currentLinks = existingLinks.filter((l) => l.src_id === recordId && l.link_type === rel.key);
      // Remove deselected FIRST (lookup relationships only — master_detail
      // unlinks are refused by the engine), so a changed cardinality-'one'
      // relationship frees its single slot before the new link is added.
      if (rel.kind === 'lookup') {
        for (const l of currentLinks) {
          if (!wanted.includes(l.dst_id)) await unlinkRecord(tenantId, recordId, l.id);
        }
      }
      // Then add newly selected.
      const currentIds = currentLinks.map((l) => l.dst_id);
      for (const dstId of wanted) {
        if (!currentIds.includes(dstId)) await linkRecord(tenantId, recordId, rel.key, dstId);
      }
    }
  }

  async function submit(e: Event) {
    e.preventDefault();
    const miss = validate();
    if (miss.length > 0) {
      setMissing(new Set(miss));
      setError('Fill in the required fields.');
      return;
    }
    setMissing(new Set());
    setBusy(true);
    setError(null);
    try {
      const payload = fieldsPayload();
      let record: RecordRow;
      if (existing) {
        record = await updateRecord(tenantId, existing.record.id, payload);
      } else {
        record = await createRecord(tenantId, typeKey, payload);
      }
      await reconcileLinks(record.id);
      onSaved(record);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form class="rf" onSubmit={submit} noValidate>
      <div class="rf__title">
        {isEdit ? 'Edit' : 'New'} {schema.label}
      </div>
      {schema.fields.map((f) => (
        <FieldInput
          key={f.name}
          field={f}
          value={values[f.name]}
          onChange={(v) => setField(f.name, v)}
          tenantId={tenantId}
          invalid={missing.has(f.name)}
        />
      ))}
      {schema.relationships.map((r) => (
        <RelationshipPicker
          key={r.key}
          tenantId={tenantId}
          rel={r}
          selected={rels[r.key] ?? []}
          onChange={(ids) => setRels((s) => ({ ...s, [r.key]: ids }))}
        />
      ))}
      {error && (
        <div class="rf__error" role="alert" data-testid="rf-error">
          {error}
        </div>
      )}
      <div class="rf__actions">
        {onCancel && (
          <button type="button" class="rf__btn rf__btn--ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
        )}
        <button type="submit" class="rf__btn rf__btn--primary" disabled={busy} data-testid="rf-submit">
          {busy ? 'Saving…' : isEdit ? 'Save changes' : `Create ${schema.label}`}
        </button>
      </div>
    </form>
  );
}

function FieldInput({
  field,
  value,
  onChange,
  tenantId,
  invalid,
}: {
  field: SchemaField;
  value: unknown;
  onChange: (v: unknown) => void;
  tenantId: string;
  invalid: boolean;
}) {
  const id = `rf-${field.name}`;
  const cls = `rf__field${invalid ? ' rf__field--invalid' : ''}`;
  const label = (
    <label class="rf__label" for={id}>
      {field.label}
      {field.required && <span class="rf__req" aria-hidden="true"> *</span>}
    </label>
  );

  if (field.type === 'boolean') {
    return (
      <div class="rf__field rf__field--bool">
        <label class="rf__toggle">
          <input
            id={id}
            type="checkbox"
            checked={Boolean(value)}
            onChange={(e) => onChange((e.target as HTMLInputElement).checked)}
          />
          <span>{field.label}</span>
        </label>
      </div>
    );
  }

  if (field.type === 'picklist') {
    return (
      <div class={cls}>
        {label}
        <select
          id={id}
          class="rf__input"
          value={(value as string) ?? ''}
          onChange={(e) => onChange((e.target as HTMLSelectElement).value)}
          aria-required={field.required}
        >
          <option value="">{field.required ? 'Select…' : '—'}</option>
          {(field.picklistValues ?? []).map((opt) => (
            <option value={opt} key={opt}>
              {humanizeValue(opt)}
            </option>
          ))}
        </select>
      </div>
    );
  }

  if (field.type === 'file') {
    return <FileField id={id} field={field} value={value} onChange={onChange} tenantId={tenantId} invalid={invalid} />;
  }

  const inputType =
    field.type === 'date' ? 'date' : field.type === 'datetime' ? 'datetime-local' : field.type === 'number' || field.type === 'currency' ? 'number' : 'text';

  return (
    <div class={cls}>
      {label}
      <input
        id={id}
        class="rf__input"
        type={inputType}
        inputMode={field.type === 'currency' || field.type === 'number' ? 'decimal' : undefined}
        step={field.type === 'currency' ? '0.01' : field.type === 'number' ? 'any' : undefined}
        value={(value as string | number | undefined) ?? ''}
        onInput={(e) => onChange((e.target as HTMLInputElement).value)}
        aria-required={field.required}
      />
    </div>
  );
}

function FileField({
  id,
  field,
  value,
  onChange,
  tenantId,
  invalid,
}: {
  id: string;
  field: SchemaField;
  value: unknown;
  onChange: (v: unknown) => void;
  tenantId: string;
  invalid: boolean;
}) {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const cls = `rf__field${invalid ? ' rf__field--invalid' : ''}`;

  async function pick(e: Event) {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file) return;
    setUploading(true);
    setErr(null);
    try {
      const { ref } = await uploadRecordBlob(tenantId, file);
      onChange(ref);
    } catch (uErr) {
      setErr(uErr instanceof ApiError ? uErr.message : 'Upload failed.');
    } finally {
      setUploading(false);
    }
  }

  return (
    <div class={cls}>
      <label class="rf__label" for={id}>
        {field.label}
        {field.required && <span class="rf__req" aria-hidden="true"> *</span>}
      </label>
      <input id={id} class="rf__input" type="file" onChange={pick} disabled={uploading} />
      {uploading && <div class="rf__hint">Uploading…</div>}
      {value ? <div class="rf__hint" data-testid="rf-file-ref">Uploaded ✓</div> : null}
      {err && <div class="rf__error" role="alert">{err}</div>}
    </div>
  );
}

function RelationshipPicker({
  tenantId,
  rel,
  selected,
  onChange,
}: {
  tenantId: string;
  rel: SchemaRelationship;
  selected: string[];
  onChange: (ids: string[]) => void;
}) {
  const [options, setOptions] = useState<RecordRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const id = `rf-rel-${rel.key}`;

  useEffect(() => {
    let alive = true;
    listRecordsByType(tenantId, rel.targetType)
      .then((rows) => alive && setOptions(rows))
      .catch((e) => alive && setErr(e instanceof ApiError ? e.message : 'Could not load options.'));
    return () => {
      alive = false;
    };
  }, [tenantId, rel.targetType]);

  const opts = useMemo(() => options ?? [], [options]);

  if (err) return <div class="rf__field rf__error" role="alert">{err}</div>;

  if (rel.cardinality === 'many') {
    return (
      <fieldset class="rf__field rf__field--rel">
        <legend class="rf__label">{rel.label}</legend>
        {opts.length === 0 ? (
          <div class="rf__hint">No {rel.targetType} records yet.</div>
        ) : (
          <div class="rf__checks">
            {opts.map((o) => (
              <label class="rf__check" key={o.id}>
                <input
                  type="checkbox"
                  checked={selected.includes(o.id)}
                  onChange={(e) => {
                    const on = (e.target as HTMLInputElement).checked;
                    onChange(on ? [...selected, o.id] : selected.filter((x) => x !== o.id));
                  }}
                />
                <span>{o.display_name || o.id}</span>
              </label>
            ))}
          </div>
        )}
      </fieldset>
    );
  }

  return (
    <div class="rf__field rf__field--rel">
      <label class="rf__label" for={id}>
        {rel.label}
      </label>
      <select
        id={id}
        class="rf__input"
        value={selected[0] ?? ''}
        onChange={(e) => {
          const v = (e.target as HTMLSelectElement).value;
          onChange(v ? [v] : []);
        }}
      >
        <option value="">—</option>
        {opts.map((o) => (
          <option value={o.id} key={o.id}>
            {o.display_name || o.id}
          </option>
        ))}
      </select>
    </div>
  );
}

/** Turns a picklist raw value ('screen_test') into a readable option label. */
function humanizeValue(v: string): string {
  return v
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}
